import { useState, useRef, useEffect, useCallback } from "react";
import { useAllModulesConfig } from "@/hooks/useAuthConfig";
import { isAdminPreview } from "@/hooks/useModuleVisibility";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, MapPin, Stethoscope, Building2, Pill, FlaskConical,
  Truck, Droplets, Heart, CalendarHeart, Package, Dumbbell, Sparkles, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchAutocomplete from "@/components/SearchAutocomplete";
import CityAutocomplete from "@/components/CityAutocomplete";
import { generateListingUrl } from "@/lib/seo";

type Tab = {
  key: string;
  label: string;
  icon: typeof Stethoscope;
  category?: number;
  path?: string;
  placeholder: string;
};

const ALL_TABS: Tab[] = [
  { key: "doctors",    label: "Doctors",      icon: Stethoscope,  category: 2,  placeholder: "Search by speciality, doctor, condition…" },
  { key: "hospitals",  label: "Hospitals",    icon: Building2,    category: 1,  placeholder: "Multispeciality, cancer, eye, IVF…" },
  { key: "pharmacy",   label: "Pharmacy",     icon: Pill,         category: 3,  placeholder: "24x7 pharmacy, ayurvedic, surgical…" },
  { key: "diagnostics",label: "Diagnostics",  icon: FlaskConical, category: 6,  placeholder: "MRI, CT scan, pathology, X-ray…" },
  { key: "ambulance",  label: "Ambulance",    icon: Truck,        path: "/ambulance",    placeholder: "Search ambulance services near you" },
  { key: "blood",      label: "Blood Banks",  icon: Droplets,     path: "/blood-banks",  placeholder: "Search blood banks by group / city" },
  { key: "homecare",   label: "HomeCare+",    icon: Heart,        path: "/homecare",     placeholder: "Nurse, physio, elderly care…" },
  { key: "camps",      label: "Health Camps", icon: CalendarHeart,path: "/health-camps", placeholder: "Eye, dental, cardiac camps…" },
  { key: "wholesaler", label: "Wholesaler",   icon: Package,      category: 7,  placeholder: "Bulk medicines, surgical supply…" },
  { key: "gym",        label: "Gym",          icon: Dumbbell,     category: 11, placeholder: "Fitness, gym, trainer — coming soon" },
];

const TAB_MODULE_KEY: Record<string, string> = {
  doctors: "doctor", hospitals: "hospital", pharmacy: "medical_store",
  diagnostics: "diagnostic", ambulance: "ambulance", blood: "blood_bank",
  homecare: "homecare", camps: "health_camp", wholesaler: "wholesaler",
};

// Real, city/specialty-wise search-intent data — the actual answer to "which
// keywords are people searching for in Gwalior/Delhi/Mumbai" that no generic
// keyword tool can give, since it's this site's own visitors. Called from
// both the Search-button submit AND picking an autocomplete suggestion
// (onSuggestion) — the latter is at least as common a way to search as
// typing+clicking Search, and was missing this the first time around. GA4
// doesn't act on a dataLayer push by itself — someone with GTM access still
// has to add a trigger (Custom Event, event name "site_search") and a GA4
// Event tag reading these same field names, same as the "Google Tag"
// already set up this session.
const trackSiteSearch = (term: string, category: string, cityName?: string) => {
  if (!term && !cityName) return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({
    event: "site_search",
    search_term: term || undefined,
    search_category: category,
    search_city: cityName || undefined,
  });
};

const SmartSearchTabs = () => {
  const navigate = useNavigate();
  const moduleConfig = useAllModulesConfig();
  const adminPreview = isAdminPreview();
  const TABS = ALL_TABS.filter(t => {
    if (adminPreview) return true;
    const mk = TAB_MODULE_KEY[t.key];
    if (!mk) return true;
    const status = moduleConfig?.[mk]?.visibility_status ?? 'LIVE';
    return status === 'LIVE';
  });
  const [activeTab, setActiveTab] = useState<Tab>(TABS[0] ?? ALL_TABS[0]);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [selectedCity, setSelectedCity] = useState<{ id: number | string; title: string; stateId?: number | string } | null>(null);

  // Category tabs scroll further than one screen (10 categories, ~6 fit) but the row hides its
  // own scrollbar for a clean look — with no other cue, users never realize there's more to see.
  // These edge fades only show on the side that actually has more content, and disappear once
  // fully scrolled that way, so they read as "more here" rather than static decoration.
  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const updateTabFades = useCallback(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 4);
    setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateTabFades();
    window.addEventListener("resize", updateTabFades);
    return () => window.removeEventListener("resize", updateTabFades);
  }, [updateTabFades, TABS.length]);

  const submit = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("search", q.trim());
    if (activeTab.category) params.set("category", String(activeTab.category));
    if (selectedCity?.id) {
      params.set("city", String(selectedCity.id));
      if (selectedCity.stateId) params.set("state_id", String(selectedCity.stateId));
    } else if (city.trim()) {
      params.set("city", city.trim());
    }

    trackSiteSearch(q.trim(), activeTab.label, selectedCity?.title || city.trim());

    if (activeTab.path) {
      navigate(`${activeTab.path}${params.toString() ? `?${params.toString()}` : ""}`);
    } else {
      navigate(`/listings${params.toString() ? `?${params.toString()}` : ""}`);
    }
  };

  const onSuggestion = (s: any) => {
    const isSpec = s.type === "speciality" || s.type === "degree" || s.id < 0;
    if (isSpec) {
      trackSiteSearch(s.name, activeTab.label, selectedCity?.title || city.trim());
      const p = new URLSearchParams();
      p.set("search", s.name);
      if (activeTab.category) p.set("category", String(activeTab.category));
      navigate(`/listings?${p.toString()}`);
      return;
    }
    navigate(generateListingUrl(s.id, s.name, s.categoryType, s.city, s.state));
  };

  return (
    <div className="relative w-full max-w-5xl mx-auto">
      <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 via-healing-green/30 to-primary/30 rounded-[2rem] blur-2xl opacity-40 pointer-events-none" />

      <div className="relative bg-white/95 backdrop-blur-2xl border border-white/60 rounded-[1.75rem] sm:rounded-[2rem] p-2 sm:p-2.5 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)]">
        {/* Tabs */}
        <div className="relative">
          <div
            ref={tabsScrollRef}
            onScroll={updateTabFades}
            className="flex gap-1.5 overflow-x-auto no-scrollbar px-2 pt-2 pb-1.5"
          >
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = activeTab.key === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t)}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-pill text-xs sm:text-sm font-medium transition-all ${
                    active
                      ? "text-white shadow-md"
                      : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                  }`}
                  style={active ? { background: "var(--ui-gradient-cta, var(--gradient-cta))" } : undefined}
                >
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
          {/* Edge fades — signal "more categories this way" without a visible scrollbar */}
          <div
            className={`pointer-events-none absolute left-0 top-0 bottom-1.5 w-8 bg-gradient-to-r from-white/95 to-transparent transition-opacity duration-200 ${showLeftFade ? "opacity-100" : "opacity-0"}`}
          />
          <div
            className={`pointer-events-none absolute right-0 top-0 bottom-1.5 w-10 bg-gradient-to-l from-white/95 to-transparent transition-opacity duration-200 flex items-center justify-end pr-1 ${showRightFade ? "opacity-100" : "opacity-0"}`}
          >
            <motion.div
              animate={{ x: [0, 3, 0] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              className="text-primary/60"
            >
              <ChevronRight className="w-4 h-4" />
            </motion.div>
          </div>
        </div>

        {/* Search row */}
        <div className="flex flex-col md:flex-row gap-2 mt-1.5">
          <div className="flex-1 relative">
            <SearchAutocomplete
              value={q}
              onChange={setQ}
              onSelect={onSuggestion}
              onKeyPress={(e) => e.key === "Enter" && submit()}
              placeholder={activeTab.placeholder}
            />
          </div>
          <CityAutocomplete
            className="md:max-w-[200px]"
            value={city}
            onChange={(v) => { setCity(v); setSelectedCity(null); }}
            onSelect={(c) => { setSelectedCity(c); setCity(c.title); }}
            onKeyPress={(e) => e.key === "Enter" && submit()}
            placeholder="City"
          />
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              onClick={submit}
              className="h-14 px-8 sm:px-10 text-white text-base font-semibold rounded-pill shadow-lg w-full md:w-auto"
              style={{ background: "var(--ui-gradient-cta, var(--gradient-cta))" }}
            >
              <Search className="w-5 h-5 mr-2" />
              Search
            </Button>
          </motion.div>
        </div>

        {/* Trending row */}
        <div className="flex items-center gap-2 px-2 pt-3 pb-1 overflow-x-auto no-scrollbar">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-foreground/70 shrink-0">
            <Sparkles className="w-3 h-3 text-cta-orange animate-pulse" /> Trending:
          </span>
          {["Cardiologist", "Dentist", "MRI Scan", "24x7 Pharmacy", "Blood A+", "Air Ambulance", "Physiotherapist"].map((s) => (
            <button
              key={s}
              onClick={() => {
                setQ(s);
                const params = new URLSearchParams();
                params.set("search", s);
                if (activeTab.category) params.set("category", String(activeTab.category));
                navigate(`/listings?${params.toString()}`);
              }}
              className="shrink-0 text-[11px] px-3 py-1 rounded-pill bg-primary/10 border border-primary/20 text-primary/80 hover:bg-primary/20 hover:text-primary hover:border-primary/30 transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SmartSearchTabs;
